// src/widgets/table/TableStackAdapter.ts
/** Класс карточного режима на обёртке .im-table */
const TABLE_STACKED_CLASS = "im-table-stacked";
/** CSS-переменная порога ширины таблицы (px), ниже которого строки идут карточками */
const TABLE_STACK_BREAKPOINT_VAR = "--table-stack-breakpoint";
/** CSS-переменная минимальной удобной ширины колонки (px) */
const TABLE_STACK_COLUMN_MIN_VAR = "--table-stack-column-min";
/**
 * 2026-09-21: адаптивный режим таблицы — на узкой ширине строки показываются карточками.
 *
 * Делает две вещи:
 * 1) проставляет ячейкам data-label с текстом заголовка их колонки (строку рисует
 *    сервер, подписей в ней нет). Строки меняются из многих мест виджета (reload,
 *    reloadRow, insert, edit, строка фильтра), поэтому подписи обновляет один
 *    MutationObserver, а не каждое место вставки;
 * 2) переключает класс im-table-stacked по ширине самой таблицы, а не экрана:
 *    так карточками становятся и таблицы в узких панелях. Порог берётся из
 *    CSS-переменной --table-stack-breakpoint (только px). Нет переменной или
 *    значение 0 — режим выключен: стили проекта его не описывают.
 *
 * 2026-09-21: сколько колонок помещается, зависит от ширины: на телефоне таблица из
 * 2-3 колонок читается нормально, на планшете - и из 5. Поэтому ниже порога карточки
 * включаются, только когда колонкам не хватает места:
 *   ширина таблицы < колонки × --table-stack-column-min.
 * Порог --table-stack-breakpoint - верхняя граница: шире него карточек нет, иначе
 * широкая таблица на десктопе тоже уходила бы в карточки.
 * Колонки считаются по строке заголовков с учётом colspan; колонки без заголовка
 * (чекбокс, иконка, кнопка) узкие и не считаются. Без подписанных колонок
 * карточки не включаются - подписывать нечем.
 */
class TableStackAdapter {
    /**
     * @param onChange вызывается после смены режима. 2026-09-21: TableWidget пересчитывает
     *   ширину панели пагинации: на resize она считается раньше, чем включаются карточки
     *   (переключение идёт в следующем кадре), и по широкой ещё таблице панели ставится
     *   фиксированная width, которая потом держит страницу шире экрана.
     */
    constructor(root, onChange) {
        this.root = root;
        this.onChange = onChange;
        this.frame = 0;
        /** Число колонок с заголовком, 0 — заголовков нет */
        this.columns = 0;
        this.applyLabels();
        if (typeof MutationObserver !== "undefined") {
            // Заголовки тоже могут прийти позже (отложенная загрузка) — пересчитать и режим
            this.mo = new MutationObserver(() => {
                this.applyLabels();
                this.updateStacked();
            });
            this.mo.observe(root, { childList: true, subtree: true });
        }
        // Первый расчёт синхронно, чтобы широкая таблица не мелькнула на мобильном
        this.updateStacked();
        if (typeof ResizeObserver !== "undefined") {
            // Смена класса меняет высоту таблицы; переключение в следующем кадре,
            // иначе ResizeObserver ругается на цикл внутри своего колбэка
            this.ro = new ResizeObserver(() => {
                if (this.frame)
                    cancelAnimationFrame(this.frame);
                this.frame = requestAnimationFrame(() => this.updateStacked());
            });
            this.ro.observe(root);
        }
    }
    /** Порог из CSS-переменной; 0 — режим выключен */
    breakpoint() {
        return this.cssNumber(TABLE_STACK_BREAKPOINT_VAR, 0);
    }
    /** Минимальная ширина колонки из CSS-переменной; без переменной — ниже порога всегда карточки */
    columnMin() {
        return this.cssNumber(TABLE_STACK_COLUMN_MIN_VAR, Infinity);
    }
    /** Включить/выключить карточный режим по ширине таблицы и числу колонок */
    updateStacked() {
        const width = this.root.clientWidth;
        // Скрытая таблица (неактивная вкладка) ширины не имеет — состояние не трогаем
        if (width <= 0)
            return;
        const bp = this.breakpoint();
        const stacked = bp > 0 && width < bp
            && this.columns > 0 && width < this.columns * this.columnMin();
        if (this.root.classList.contains(TABLE_STACKED_CLASS) === stacked)
            return;
        this.root.classList.toggle(TABLE_STACKED_CLASS, stacked);
        this.onChange?.(stacked);
    }
    cssNumber(name, fallback) {
        const value = parseFloat(getComputedStyle(this.root).getPropertyValue(name));
        return Number.isFinite(value) && value > 0 ? value : fallback;
    }
    /** Проставить ячейкам data-label по заголовкам колонок */
    applyLabels() {
        // Первая таблица в обёртке — своя; вложенные таблицы идут после неё
        const table = this.root.querySelector("table");
        const header = table && this.headerRow(table);
        if (!table || !header) {
            this.columns = 0;
            return;
        }
        // Подпись по номеру колонки с учётом colspan заголовка
        const labels = [];
        for (const cell of Array.from(header.cells)) {
            const text = (cell.textContent || "").replace(/\s+/g, " ").trim();
            for (let i = 0; i < Math.max(1, cell.colSpan); i++)
                labels.push(text);
        }
        this.columns = labels.filter(Boolean).length;
        const rows = [];
        if (table.tHead)
            rows.push(...Array.from(table.tHead.rows).filter(r => r !== header));
        for (const body of Array.from(table.tBodies))
            rows.push(...Array.from(body.rows).filter(r => r !== header));
        if (table.tFoot)
            rows.push(...Array.from(table.tFoot.rows));
        for (const row of rows) {
            let col = 0;
            for (const cell of Array.from(row.cells)) {
                // Ячейка через несколько разных колонок (служебная строка кнопок tr.f,
                // строка группы) подписи не получает: она не принадлежит одной колонке
                const span = Math.max(1, cell.colSpan);
                const spanned = new Set(labels.slice(col, col + span));
                const label = spanned.size === 1 ? (labels[col] ?? "") : "";
                if (label) {
                    if (cell.dataset.label !== label)
                        cell.dataset.label = label;
                }
                else if (cell.hasAttribute("data-label")) {
                    cell.removeAttribute("data-label");
                }
                col += span;
            }
        }
    }
    /** Строка заголовков колонок. Строка фильтра тоже с классом h и при filter-above идёт первой */
    headerRow(table) {
        const isHeader = (r) => r.classList.contains("h") && !r.classList.contains("im-table-filter");
        const inHead = table.tHead && Array.from(table.tHead.rows).find(isHeader);
        if (inHead)
            return inHead;
        for (const body of Array.from(table.tBodies)) {
            const row = Array.from(body.rows).find(isHeader);
            if (row)
                return row;
        }
        return null;
    }
    destroy() {
        this.mo?.disconnect();
        this.ro?.disconnect();
        if (this.frame)
            cancelAnimationFrame(this.frame);
        this.mo = undefined;
        this.ro = undefined;
    }
}

export { TABLE_STACKED_CLASS, TABLE_STACK_BREAKPOINT_VAR, TABLE_STACK_COLUMN_MIN_VAR, TableStackAdapter };
